const CONNECTIVITY_CHECK_TIMEOUT_MS = 5000;

export class NetworkMonitor {
    private listeners: ((isOnline: boolean) => void) | null = null;
    private isOnline = navigator.onLine;
    private checkInProgress: Promise<boolean> | null = null;

    constructor() {
        window.addEventListener('online', () => this.handleBrowserOnline());
        window.addEventListener('offline', () => this.setOnlineStatus(false));
    }

    getStatus(): boolean {
        return this.isOnline;
    }

    addListener(listener: (isOnline: boolean) => void) {
        this.listeners = listener;
    }

    removeListener() {
        this.listeners = null;
    }

    shouldFetchToday(): boolean {
        const today = new Date().toDateString();
        const lastFetch = sessionStorage.getItem('lastConfigFetch');

        return lastFetch !== today;
    }

    async checkConnectivity(): Promise<boolean> {
        if (this.checkInProgress) {
            return this.checkInProgress;
        }

        this.checkInProgress = this.performConnectivityCheck();

        try {
            return await this.checkInProgress;
        } finally {
            this.checkInProgress = null;
        }
    }

    private async handleBrowserOnline(): Promise<void> {
        await this.checkConnectivity();
    }

    private async performConnectivityCheck(): Promise<boolean> {
        if (!navigator.onLine) {
            this.setOnlineStatus(false);
            return false;
        }

        const baseUrl = import.meta.env.VITE_API_INTERNAL_URL;

        if (!baseUrl) {
            this.setOnlineStatus(true);
            return true;
        }

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), CONNECTIVITY_CHECK_TIMEOUT_MS);

            const response = await fetch(baseUrl, {
                method: 'HEAD',
                cache: 'no-store',
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            const online = response.ok || response.status < 500;
            this.setOnlineStatus(online);
            return online;
        } catch {
            const online = navigator.onLine;
            this.setOnlineStatus(online);
            return online;
        }
    }

    private setOnlineStatus(isOnline: boolean) {
        if (this.isOnline !== isOnline) {
            this.isOnline = isOnline;
            if (this.listeners) {
                this.listeners(isOnline);
            }
        }
    }
}

export const networkMonitor = new NetworkMonitor();
